/* Simulateur causal — Brique 1 du moteur prédictif Noam.
 *
 * L'utilisateur choisit un acteur du graphe causal (chain node ou utility
 * actor), applique un choc (disparition, montée en gamme, rachat, alliance…),
 * et Noam propage l'onde à travers le graphe entier en calculant l'impact
 * sur chaque voisin atteint.
 *
 * Positionnement UI (invariant) : cette page est un OUTIL TRANSVERSAL.
 * Elle ne vit ni dans Pipeline ni dans Origination — elle s'accroche
 * dessus mais reste à part, comme les Cartographies 360°.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Loader2, Waves, Compass, Sparkles, GitBranch } from "lucide-react";
import ConfidenceBadge from "@/components/ConfidenceBadge";

export default function CausalSimulator() {
  const [sources, setSources] = useState([]);
  const [perturbations, setPerturbations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceId, setSourceId] = useState("");
  const [perturbation, setPerturbation] = useState("disappears");
  const [customDesc, setCustomDesc] = useState("");
  const [maxDepth, setMaxDepth] = useState(3);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [rs, rp] = await Promise.all([
          api.get("/workspace/causal-propagation/sources"),
          api.get("/workspace/causal-propagation/catalog"),
        ]);
        setSources(rs.data?.items || []);
        setPerturbations(rp.data?.perturbations || []);
      } catch (e) {
        notify.error(fmtError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const run = async () => {
    if (!sourceId) {
      notify.error("Choisissez d'abord un acteur source.");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/workspace/causal-propagation/simulate", {
        source_type: sources.find((s) => s.id === sourceId)?.kind || "chain_node",
        source_id: sourceId,
        perturbation,
        custom_desc: perturbation === "custom" ? customDesc : undefined,
        max_depth: maxDepth,
      });
      setResult(r.data);
    } catch (e) {
      notify.error(fmtError(e));
    } finally {
      setRunning(false);
    }
  };

  const cascadeByDepth = useMemo(() => {
    const buckets = {};
    (result?.cascade || []).forEach((c) => {
      const d = c.depth || 1;
      if (!buckets[d]) buckets[d] = [];
      buckets[d].push(c);
    });
    return buckets;
  }, [result]);

  return (
    <>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] font-semibold">Simulateur causal · Brique prédictive</div>
          <h1 className="noam-display text-2xl mt-1">Et si… — la boussole des chocs.</h1>
          <p className="text-xs text-zinc-400 mt-1 max-w-2xl">
            Choisissez un acteur de votre graphe causal (chaîne de valeur, utility mapping) et appliquez-lui un choc : disparition, rachat, montée en gamme, alliance inattendue. Noam propage l&apos;onde à travers vos dépendances et vous montre <span className="text-white font-semibold">qui bougera</span>, <span className="text-white font-semibold">avec quelle intensité</span> et <span className="text-white font-semibold">par quel chemin causal</span>.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-[11px] text-zinc-500 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Chargement du graphe causal…
        </div>
      ) : sources.length === 0 ? (
        <div className="noam-card p-10 text-center space-y-3" data-testid="simulator-empty">
          <Waves className="w-8 h-8 mx-auto text-zinc-600" />
          <div className="text-sm text-white font-semibold">Votre graphe causal est encore vide.</div>
          <p className="text-[11px] text-zinc-400 max-w-md mx-auto">
            Pour utiliser le simulateur, générez d&apos;abord votre <span className="text-[#EF9F27]">chaîne de valeur (Origination)</span> et votre <span className="text-[#EF9F27]">utility mapping</span> — ce sont les deux sources qui alimentent le graphe.
          </p>
        </div>
      ) : (
        <>
          {/* Configuration du choc */}
          <div className="noam-card p-4 space-y-3" data-testid="simulator-config">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-2">
              <Compass className="w-3 h-3 text-[#EF9F27]" /> Configuration du choc
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Acteur source ({sources.length})</label>
                <select
                  value={sourceId}
                  onChange={(e) => setSourceId(e.target.value)}
                  className="w-full text-[12px] bg-white/[0.03] border border-white/15 rounded px-2 py-1.5 text-white focus:outline-none focus:border-white/30"
                  data-testid="simulator-source-select"
                >
                  <option value="">— choisir un acteur —</option>
                  {sources.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} · {s.kind === "chain_node" ? "chaîne" : "utility"}{s.role ? ` (${s.role})` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Type de choc</label>
                <select
                  value={perturbation}
                  onChange={(e) => setPerturbation(e.target.value)}
                  className="w-full text-[12px] bg-white/[0.03] border border-white/15 rounded px-2 py-1.5 text-white focus:outline-none focus:border-white/30"
                  data-testid="simulator-perturbation-select"
                >
                  {perturbations.map((p) => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {perturbation === "custom" && (
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Description du choc sur mesure</label>
                <textarea
                  value={customDesc}
                  onChange={(e) => setCustomDesc(e.target.value)}
                  placeholder="Ex. Cet acteur devient un fabricant de puces IA en 12 mois et remonte de 2 niveaux dans la chaîne…"
                  rows={3}
                  className="w-full text-[12px] bg-white/[0.03] border border-white/15 rounded px-2 py-1.5 text-white focus:outline-none focus:border-white/30 resize-none"
                  data-testid="simulator-custom-desc"
                  maxLength={400}
                />
              </div>
            )}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-[10px] text-zinc-500">
                <span>Profondeur de propagation :</span>
                {[1, 2, 3, 4, 5].map((d) => (
                  <button
                    key={d}
                    onClick={() => setMaxDepth(d)}
                    className={`px-2 py-0.5 rounded border text-[10px] ${maxDepth === d ? "border-[#EF9F27] text-[#EF9F27] bg-[#EF9F27]/10" : "border-white/10 text-zinc-400 hover:border-white/25"}`}
                    data-testid={`simulator-depth-${d}`}
                  >{d}</button>
                ))}
              </div>
              <button
                onClick={run}
                disabled={running || !sourceId}
                className="text-[11px] px-3 py-1.5 rounded bg-[#EF9F27] text-black font-semibold hover:bg-[#EF9F27]/90 inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="simulator-run-btn"
              >
                {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Lancer la simulation
              </button>
            </div>
          </div>

          {/* Résultat */}
          {result && (
            <div className="space-y-3" data-testid="simulator-result">
              {/* Lecture stratégique */}
              <div className="noam-card p-4 border border-[#EF9F27]/30">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] font-semibold">
                      Choc : {result.perturbation_label}
                    </div>
                    <div className="text-lg font-bold text-white mt-1">
                      {result.reading?.title || `Impact sur ${result.reach} acteur${result.reach > 1 ? "s" : ""} du graphe`}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                      Source : <span className="text-white font-semibold">{result.source.name}</span> · Portée : {result.reach} / {result.graph_size} nœuds
                    </div>
                  </div>
                  <ConfidenceBadge
                    value={result.confidence}
                    rationale={result.reading?.title || "Confiance calculée à partir de la richesse du graphe et de l'étendue de la propagation."}
                    assumptions={result.assumptions}
                    caveats={result.caveats}
                    size="lg"
                  />
                </div>

                {result.reading?.text && (
                  <p className="text-[12px] text-zinc-300 leading-snug mt-3">{result.reading.text}</p>
                )}
                {result.reading_error && (
                  <p className="text-[11px] text-[#E24B4A] mt-3">⚠ {result.reading_error}</p>
                )}

                {result.reading?.top_actions?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <div className="text-[10px] uppercase tracking-wider text-[#1D9E75] font-semibold mb-1.5">Actions concrètes à envisager</div>
                    <ul className="space-y-1">
                      {result.reading.top_actions.map((a, i) => (
                        <li key={i} className="text-[11.5px] text-zinc-300 leading-snug flex gap-1.5">
                          <span className="text-[#1D9E75] flex-shrink-0">→</span><span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Cascade par profondeur */}
              <div className="noam-card p-4">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold text-[#7F77DD] mb-3">
                  <GitBranch className="w-3 h-3" />
                  Cascade d&apos;impact par profondeur causale
                </div>
                {Object.keys(cascadeByDepth).length === 0 ? (
                  <div className="text-[11px] text-zinc-500">
                    Aucun voisin atteint — l&apos;acteur source n&apos;est pas relié au reste du graphe. Enrichissez vos relations dans l&apos;Utility Mapping ou la chaîne de valeur pour rendre la propagation possible.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.keys(cascadeByDepth).sort((a, b) => Number(a) - Number(b)).map((d) => (
                      <div key={d}>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                          Profondeur {d} · {cascadeByDepth[d].length} acteur{cascadeByDepth[d].length > 1 ? "s" : ""} atteint{cascadeByDepth[d].length > 1 ? "s" : ""}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {cascadeByDepth[d].map((c) => {
                            const intensity = c.impact_score;
                            const color = intensity > 40
                              ? "text-[#E24B4A]"
                              : intensity > 20
                                ? "text-[#EF9F27]"
                                : "text-[#378ADD]";
                            return (
                              <div key={c.id} className="border border-white/10 rounded p-2.5 bg-white/[0.02] min-w-0" data-testid={`simulator-cascade-node-${c.id}`}>
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0 flex-1">
                                    <div className="text-[12px] text-white font-semibold truncate">{c.name}</div>
                                    <div className="text-[9.5px] text-zinc-500 truncate">
                                      {c.kind === "chain_node" ? "chaîne" : "utility"}{c.role ? ` · ${c.role}` : ""}
                                    </div>
                                  </div>
                                  <span className={`text-[10px] font-semibold ${color} tabular-nums flex-shrink-0`}>
                                    {intensity.toFixed(0)}%
                                  </span>
                                </div>
                                <div className="mt-1.5">
                                  <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                                    <div className={`h-full rounded-full ${color.replace("text-", "bg-")}`} style={{ width: `${Math.min(100, intensity)}%` }} />
                                  </div>
                                </div>
                                {c.path_names?.length > 1 && (
                                  <div className="text-[9.5px] text-zinc-500 mt-1.5 truncate" title={c.path_names.join(" → ")}>
                                    via {c.path_names.slice(1, -1).join(" → ") || "arête directe"}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
